import type { ComponentType, SVGProps } from "react";
import { REGEX, safeSliceGraphemes } from "@app/helpers";
import {
  getIrregularMaintenanceItemOptionsForCategory,
  getIrregularMaintenanceItemOtherChoiceId,
  getMaintenanceCategoryCode,
  getMaintenanceTypeCode,
  isFiltersMaintenanceCategory,
  isIrregularMaintenanceItemOtherChoice,
  isOthersMaintenanceCategory,
  MAINTENANCE_CATEGORY_FILTERS_CODE,
  MAINTENANCE_CATEGORY_OTHERS_CODE,
  mapMaintenanceTypesToOptions,
  resolveIrregularMaintenanceItemForFiltersType,
  shouldHideMaintenanceRefillReplaceFields,
} from "@app/api/maintenance/maintenance.api";
import { ReactComponent as IconAxleOil } from "./ItemsIcons/AXLE_OIL.svg";
import { ReactComponent as IconCoolant } from "./ItemsIcons/COOLANT.svg";
import { ReactComponent as IconDamperCaseOil } from "./ItemsIcons/DAMPER_CASE_OIL.svg";
import { ReactComponent as IconEngineOil } from "./ItemsIcons/ENGINE_OIL.svg";
import { ReactComponent as IconFinalCaseOil } from "./ItemsIcons/FINAL_CASE_OIL.svg";
import { ReactComponent as IconHydraulicOil } from "./ItemsIcons/HYDRAULIC_OIL.svg";
import { ReactComponent as IconMachineryCaseOil } from "./ItemsIcons/MACHINERY_CASE_OIL.svg";
import { ReactComponent as IconTransmissionOil } from "./ItemsIcons/TRANSMISSION_OIL.svg";
import { COMMENT_MAX_LENGTH, MaintenanceResultType } from "@app/constants";
import {
  MachineReportMedia,
  MachineReportRecording,
} from "@app/types/machine.types";

type SvgIconComponent = ComponentType<SVGProps<SVGSVGElement>>;

const MAINTENANCE_ITEM_ICONS: Record<string, SvgIconComponent> = {
  AXLE_OIL: IconAxleOil,
  COOLANT: IconCoolant,
  DAMPER_CASE_OIL: IconDamperCaseOil,
  ENGINE_OIL: IconEngineOil,
  FINAL_CASE_OIL: IconFinalCaseOil,
  HYDRAULIC_OIL: IconHydraulicOil,
  MACHINERY_CASE_OIL: IconMachineryCaseOil,
  TRANSMISSION_OIL: IconTransmissionOil,
};

export function getMaintenanceItemIcon(
  code: string | null | undefined,
): SvgIconComponent | null {
  if (!code) return null;
  return MAINTENANCE_ITEM_ICONS[code] ?? null;
}

export {
  getIrregularMaintenanceItemOptionsForCategory,
  getIrregularMaintenanceItemOtherChoiceId,
  getMaintenanceCategoryCode,
  getMaintenanceTypeCode,
  isFiltersMaintenanceCategory,
  isIrregularMaintenanceItemOtherChoice,
  isOthersMaintenanceCategory,
  MAINTENANCE_CATEGORY_FILTERS_CODE,
  MAINTENANCE_CATEGORY_OTHERS_CODE,
  mapMaintenanceTypesToOptions,
  resolveIrregularMaintenanceItemForFiltersType,
  shouldHideMaintenanceRefillReplaceFields,
};

export const MAINTENANCE_ITEM_MEDIA_LIMIT = 5;
export const MAINTENANCE_ITEMS_MEDIA_TOTAL_LIMIT = 50;

export function countMaintenanceItemsMediaTotal(
  items: MaintenanceItemEntry[],
  excludeItemId?: string,
): number {
  return items.reduce((sum, item) => {
    if (excludeItemId && item.id === excludeItemId) return sum;
    return (
      sum + item.mediaFiles.length + (item.existingItemMedias?.length ?? 0)
    );
  }, 0);
}
export function isMaintenanceItemsMediaTotalAtLimit(count: number): boolean {
  return count >= MAINTENANCE_ITEMS_MEDIA_TOTAL_LIMIT;
}

export const MAINTENANCE_SELECT_FORM_CONTROL_SX = {
  mt: 0.5,
  overflow: "visible",
} as const;

const MAINTENANCE_DARK_BLUE = "#140a9a";

export const MAINTENANCE_ACTION_RADIO_SX = {
  color: "#939394",
  "&.Mui-checked": {
    color: MAINTENANCE_DARK_BLUE,
  },
} as const;

export const MAINTENANCE_SELECT_MENU_PROPS = {
  disablePortal: false,
  anchorOrigin: {
    vertical: "bottom" as const,
    horizontal: "left" as const,
  },
  transformOrigin: {
    vertical: "top" as const,
    horizontal: "left" as const,
  },
  PaperProps: {
    sx: { zIndex: 1700, maxHeight: 280 },
  },
};

export type MaintenanceItemAction = "refill" | "replace";

export function isMaintenanceItemTextFieldInvalid(value: string): boolean {
  return (
    !REGEX.commonNameFieldCharacterMuiltiLineRegex.test(value) ||
    !REGEX.emojiRegex.test(value)
  );
}

export function isMaintenanceCommentWithinLimit(value: string): boolean {
  const trimmed = value.trim();
  return safeSliceGraphemes(trimmed, COMMENT_MAX_LENGTH) === trimmed;
}

export const REFILL_AMOUNT_MAX = 999999.9;

const NUMERIC_INPUT_PATTERN = /^\d*\.?\d{0,1}$/;

export function stripLeadingZerosFromNumeric(value: string): string {
  if (!value) return value;
  if (value.startsWith("0.")) return value;
  if (value.includes(".")) {
    const [intPart, ...decParts] = value.split(".");
    const decPart = decParts.join("").replace(/\./g, "");
    const strippedInt = intPart.replace(/^0+/, "") || "0";
    if (value.endsWith(".") && decPart === "") {
      return `${strippedInt}.`;
    }
    return `${strippedInt}.${decPart}`;
  }
  return value.replace(/^0+/, "") || "0";
}

export function enforceNumericDecimalFormat(value: string): string {
  const dotIndex = value.indexOf(".");
  if (dotIndex === -1) return value;
  const intPart = value.slice(0, dotIndex);
  const decPart = value.slice(dotIndex + 1).replace(/\./g, "");
  if (value.endsWith(".") && decPart === "") {
    return `${intPart}.`;
  }
  return `${intPart}.${decPart.slice(0, 1)}`;
}

export function sanitizeNumericInputValue(
  raw: string,
  fallback: string,
): string {
  if (!NUMERIC_INPUT_PATTERN.test(raw)) {
    return fallback;
  }
  return enforceNumericDecimalFormat(stripLeadingZerosFromNumeric(raw));
}

export function isNumericValueValid(value: string, max: number): boolean {
  const trimmed = value.trim();
  if (!trimmed || trimmed.endsWith(".")) {
    return false;
  }
  if (!/^\d+\.\d$/.test(trimmed) && !/^\d+$/.test(trimmed)) {
    return false;
  }
  const parsed = parseFloat(trimmed);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= max;
}

export function normalizeNumericOnBlur(value: string, max: number): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }
  let normalized = enforceNumericDecimalFormat(
    stripLeadingZerosFromNumeric(trimmed),
  );
  if (/^\d+$/.test(normalized)) {
    normalized = `${normalized}.0`;
  } else if (normalized.endsWith(".")) {
    normalized = `${normalized}0`;
  }
  const parsed = parseFloat(normalized);
  if (Number.isFinite(parsed) && parsed > max) {
    return String(max);
  }
  return normalized;
}

export type MaintenanceItemCommonFields = {
  id: string;
  resultValue?: MaintenanceResultType;
  comment: string;
  commentExpanded: boolean;
  recordedBlob: Blob | null;
  mediaFiles: File[];
  commentError?: boolean;
  fileCount?: number;
  hasUploadError?: boolean;
  maintenanceReportItemResponseId?: string;
  existingItemMedias?: MachineReportMedia[];
  existingItemRecording?: MachineReportRecording | null;
};

export type ManualMaintenanceItemEntry = MaintenanceItemCommonFields & {
  source: "manual";
  categoryId: string;
  categoryCode: string;
  categoryLabel: string;
  maintenanceTypeId: string;
  maintenanceTypeCode: string;
  maintenanceTypeLabel: string;
  maintenanceItemId: string;
  maintenanceItemLabel: string;
  maintenanceItemOther: string;
  action: MaintenanceItemAction | "";
  refillAmount: string;
};

export type ScheduledMaintenanceItemEntry = MaintenanceItemCommonFields & {
  source: "schedule";
  periodicMaintenanceItemId: string;
  periodicMaintenanceMachineItemId: string;
  itemName: string;
  work: string;
  workCode: string;
  regularMaintenanceItemChoiceId: string;
  regularMaintenanceItemChoiceCode: string;
  regularMaintenanceItemChoiceName: string;
};

export type MaintenanceItemEntry =
  | ManualMaintenanceItemEntry
  | ScheduledMaintenanceItemEntry;

export function getMaintenanceItemDisplayName(
  entry: MaintenanceItemEntry,
): string {
  if (entry.source === "schedule") {
    return entry.itemName.trim();
  }
  return entry.maintenanceItemOther.trim() || entry.maintenanceItemLabel.trim();
}

export function isScheduledMaintenanceItem(
  entry: MaintenanceItemEntry,
): entry is ScheduledMaintenanceItemEntry {
  return entry.source === "schedule";
}

export function isManualMaintenanceItemRefillAmountInvalid(
  item: MaintenanceItemEntry,
): boolean {
  if (item.source !== "manual") return false;
  if (shouldHideMaintenanceRefillReplaceFields(item.categoryCode)) return false;
  const trimmed = item.refillAmount.trim();
  if (!trimmed) return false;
  return !isNumericValueValid(trimmed, REFILL_AMOUNT_MAX);
}

export function createEmptyMaintenanceItemEntry(
  partial?: Partial<ManualMaintenanceItemEntry>,
): ManualMaintenanceItemEntry {
  return {
    source: "manual",
    id:
      partial?.id ??
      `item-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    categoryId: partial?.categoryId ?? "",
    categoryCode: partial?.categoryCode ?? "",
    categoryLabel: partial?.categoryLabel ?? "",
    maintenanceTypeId: partial?.maintenanceTypeId ?? "",
    maintenanceTypeCode: partial?.maintenanceTypeCode ?? "",
    maintenanceTypeLabel: partial?.maintenanceTypeLabel ?? "",
    maintenanceItemId: partial?.maintenanceItemId ?? "",
    maintenanceItemLabel: partial?.maintenanceItemLabel ?? "",
    maintenanceItemOther: partial?.maintenanceItemOther ?? "",
    action: partial?.action ?? "refill",
    refillAmount: partial?.refillAmount ?? "",
    comment: partial?.comment ?? "",
    recordedBlob: partial?.recordedBlob ?? null,
    mediaFiles: partial?.mediaFiles ?? [],
    commentExpanded: false,
    resultValue: partial?.resultValue,
    commentError: partial?.commentError ?? false,
    fileCount: partial?.fileCount ?? 0,
    hasUploadError: partial?.hasUploadError ?? false,
  };
}

export function arePeriodicMaintenanceFieldsValid(
  periodicSwitchOn: boolean,
  maintenanceIntervalIds: string[],
): boolean {
  if (!periodicSwitchOn) {
    return true;
  }
  return maintenanceIntervalIds.length > 0;
}
